/**
 * Input validation utilities for Hub UI
 */

export interface ValidationResult {
  isValid: boolean;
  value: number | null;
  error?: string;
  reasonCode?: string;
}

export interface NumericRange {
  min: number;
  max: number;
  unit: string;
  examples: string;
}

/**
 * Parse a string input into a finite number or null.
 * This is the boundary function - use it at onChange to convert strings to numbers.
 */
export function parseFiniteNumber(input: string): number | null {
  if (input === '' || input === undefined || input === null) {
    return null;
  }
  
  const trimmed = input.trim();
  const parsed = parseFloat(trimmed);
  
  return isNaN(parsed) || !isFinite(parsed) ? null : parsed;
}

/**
 * Clamp a number to a range or return structured error.
 */
export function clampOrError(
  value: number | null,
  min: number,
  max: number,
  unit: string,
  fieldName: string
): ValidationResult {
  if (value === null) {
    return { isValid: true, value: null };
  }
  
  if (value < min || value > max) {
    return {
      isValid: false,
      value,
      error: `${fieldName} must be between ${min} and ${max} ${unit}`,
      reasonCode: 'OUT_OF_RANGE'
    };
  }
  
  return { isValid: true, value };
}

// Define sane ranges for all numeric inputs
export const VALIDATION_RANGES = {
  mainsFlowLpm: {
    min: 1,
    max: 60,
    unit: 'L/min',
    examples: 'e.g., 18 (typical), 12 (low), 25 (good)'
  },
  mainsStaticPressureBar: {
    min: 0.5,
    max: 6,
    unit: 'bar',
    examples: 'e.g., 2.5 (typical), 1.5 (low), 4.0 (high)'
  },
  mainsDynamicFlowLpm: {
    min: 1,
    max: 60,
    unit: 'L/min',
    examples: 'e.g., 16 (typical), 10 (low), 20 (good)'
  },
  mainsDynamicPressureBar: {
    min: 0.5,
    max: 6,
    unit: 'bar',
    examples: 'e.g., 1.8 (typical), 1.0 (low), 3.0 (high)'
  }
} as const;



/**
 * Validate all DHW pressure inputs with cross-field validation
 */
export function validateDHWPressureData(data: {
  mainsFlowLpm: number | null;
  mainsStaticPressureBar: number | null;
  mainsDynamicFlowLpm: number | null;
  mainsDynamicPressureBar: number | null;
}): { isValid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  // Validate flow range
  if (data.mainsFlowLpm !== null) {
    const flowResult = clampOrError(
      data.mainsFlowLpm,
      VALIDATION_RANGES.mainsFlowLpm.min,
      VALIDATION_RANGES.mainsFlowLpm.max,
      VALIDATION_RANGES.mainsFlowLpm.unit,
      'Flow'
    );
    if (!flowResult.isValid && flowResult.error) {
      errors.mainsFlowLpm = flowResult.error;
    }
  }

  // Validate static pressure range
  if (data.mainsStaticPressureBar !== null) {
    const pressureResult = clampOrError(
      data.mainsStaticPressureBar,
      VALIDATION_RANGES.mainsStaticPressureBar.min,
      VALIDATION_RANGES.mainsStaticPressureBar.max,
      VALIDATION_RANGES.mainsStaticPressureBar.unit,
      'Static pressure'
    );
    if (!pressureResult.isValid && pressureResult.error) {
      errors.mainsStaticPressureBar = pressureResult.error;
    }
  }

  // Validate dynamic flow range (optional)
  if (data.mainsDynamicFlowLpm !== null) {
    const dynFlowResult = clampOrError(
      data.mainsDynamicFlowLpm,
      VALIDATION_RANGES.mainsDynamicFlowLpm.min,
      VALIDATION_RANGES.mainsDynamicFlowLpm.max,
      VALIDATION_RANGES.mainsDynamicFlowLpm.unit,
      'Dynamic flow'
    );
    if (!dynFlowResult.isValid && dynFlowResult.error) {
      errors.mainsDynamicFlowLpm = dynFlowResult.error;
    }
  }

  // Validate dynamic pressure range (optional)
  if (data.mainsDynamicPressureBar !== null) {
    const dynPressureResult = clampOrError(
      data.mainsDynamicPressureBar,
      VALIDATION_RANGES.mainsDynamicPressureBar.min,
      VALIDATION_RANGES.mainsDynamicPressureBar.max,
      VALIDATION_RANGES.mainsDynamicPressureBar.unit,
      'Dynamic pressure'
    );
    if (!dynPressureResult.isValid && dynPressureResult.error) {
      errors.mainsDynamicPressureBar = dynPressureResult.error;
    }
  }

  // Cross-field validation: dynamic pair must be both-or-neither
  const hasDynamicFlow = data.mainsDynamicFlowLpm !== null;
  const hasDynamicPressure = data.mainsDynamicPressureBar !== null;
  
  if (hasDynamicFlow !== hasDynamicPressure) {
    const message = 'Dynamic pair requires both flow and pressure, or leave both empty';
    if (hasDynamicFlow && !hasDynamicPressure) {
      errors.mainsDynamicPressureBar = message;
    } else {
      errors.mainsDynamicFlowLpm = message;
    }
  }

  // Cross-field validation: dynamic pressure should not exceed static pressure
  if (
    data.mainsDynamicPressureBar !== null &&
    data.mainsStaticPressureBar !== null &&
    data.mainsDynamicPressureBar > data.mainsStaticPressureBar
  ) {
    errors.mainsDynamicPressureBar = 
      'Dynamic pressure cannot exceed static pressure';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}
